import { useEffect, useState } from 'react';
import { Modal } from '../../../../../../core/components/templates/Modal';
import { Typography } from '../../../../../../core/components/molecules/Typography';
import { Button } from '../../../../../../core/components/atoms/Button';

type AdjustScheduleAction = 'edit' | 'delete';
type EditScheduleOption = 'edit-hours' | 'extend-deadline' | 'reorganize';

interface AdjustScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAction: AdjustScheduleAction;
  onSelectAction: (action: AdjustScheduleAction) => void;
  onDeleteConfirm: () => void;
  onEditConfirm: (option: EditScheduleOption) => void;
}

const radioClassName =
  'bg-mono-color-white border-mono-color-dark checked:border-color-brand-primary checked:bg-brand-color-primary-lighter checked:after:border-color-brand-primary checked:after:bg-color-brand-primary checked:focus:border-color-brand-primary';

const OptionCard = ({
  id,
  title,
  description,
  checked,
  onChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) => {
  return (
    <button
      type='button'
      onClick={onChange}
      className={`flex w-full items-start gap-md rounded-md border-hairline border-mono-color-light p-4 text-left ${
        checked ? 'bg-mono-color-lighter' : 'bg-mono-color-white'
      }`}
    >
      <input
        id={id}
        type='radio'
        checked={checked}
        onChange={onChange}
        className={`${radioClassName} relative mt-1 h-5 w-5 appearance-none rounded-circular border-thin text-mono-color-white before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:rounded-circular before:opacity-0 before:content-[''] after:absolute after:z-[0] after:block after:h-4 after:w-4 after:rounded-circular after:content-[''] checked:before:opacity-[0.16] checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-[0.625rem] checked:after:w-[0.625rem] checked:after:rounded-circular checked:after:content-[''] checked:after:[transform:translate(-50%,-50%)] hover:cursor-pointer hover:before:opacity-[0.04] focus:outline-none focus:ring-0 focus:before:scale-100 focus:before:opacity-[0.12] checked:focus:before:scale-100`}
      />
      <div className='flex flex-col gap-1'>
        <Typography weight='bold'>{title}</Typography>
        <Typography size='small'>{description}</Typography>
      </div>
    </button>
  );
};

const AdjustScheduleModal = ({
  isOpen,
  onClose,
  selectedAction,
  onSelectAction,
  onDeleteConfirm,
  onEditConfirm,
}: AdjustScheduleModalProps) => {
  const [isReorganizeConfirmOpen, setIsReorganizeConfirmOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      onSelectAction('edit');
      setIsReorganizeConfirmOpen(false);
    }
  }, [isOpen, onSelectAction]);

  const handlePrimaryAction = () => {
    if (selectedAction === 'delete') {
      onDeleteConfirm();
      return;
    }

    setIsReorganizeConfirmOpen(true);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isReorganizeConfirmOpen ? 'Reorganizar Cronograma' : 'Editar Cronograma'
      }
      noPadding
      size='md'
      className='w-full max-w-[550px]'
    >
      {isReorganizeConfirmOpen ? (
        <div className='flex flex-col'>
          <div className='px-6 py-6 text-center'>
            <Typography weight='bold'>
              Você tem certeza que deseja editar seu Cronograma?
            </Typography>
          </div>
          <div className='flex w-full items-center gap-lg px-6 py-4 shadow-level-2'>
            <Button
              variant='text'
              onClick={() => setIsReorganizeConfirmOpen(false)}
              className='flex-1 justify-center font-notoSans'
            >
              Cancelar
            </Button>
            <Button
              variant='contained'
              onClick={() => {
                setIsReorganizeConfirmOpen(false);
                onEditConfirm('reorganize');
              }}
              className='flex-1 justify-center font-notoSans'
            >
              Sim
            </Button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col'>
          <div className='flex flex-col gap-4 px-6 py-6'>
            <Typography weight='bold'>
              É normal atrasar um pouco nos estudos. Não se preocupe.
            </Typography>
            <Typography weight='bold'>O que você gostaria de fazer?</Typography>
            <div className='flex flex-col gap-3'>
              <OptionCard
                id='adjust-schedule-edit'
                title='Reorganizar cronograma'
                description='Você poderá editar as horas de estudo por dia, e as disciplinas que deseja estudar em cada dia e sua data final. Sem perder o seu progresso atual.'
                checked={selectedAction === 'edit'}
                onChange={() => onSelectAction('edit')}
              />
              <OptionCard
                id='adjust-schedule-delete'
                title='Excluir Cronograma'
                description='Você inicia um novo cronograma de estudos'
                checked={selectedAction === 'delete'}
                onChange={() => onSelectAction('delete')}
              />
            </div>
          </div>
          <div className='flex w-full items-center gap-lg px-6 py-4 shadow-level-2'>
            <Button
              variant='text'
              onClick={onClose}
              className='flex-1 justify-center font-notoSans'
            >
              Cancelar
            </Button>
            <Button
              variant='contained'
              onClick={handlePrimaryAction}
              className='flex-1 justify-center font-notoSans'
            >
              Continuar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AdjustScheduleModal;
